import styled from '@emotion/styled';
import Image from 'presentations/views/components/atoms/Image';
import {
  spacing,
  fontSize,
  fontWeight,
  mediaMax,
  isMediaMax,
} from 'presentations/views/styles';
import {
  formatServiceRestrictSummary,
  formatTaxExcludedPrice,
} from 'presentations/views/helpers/formatter';
import { black, gray01, gray04 } from 'presentations/views/styles/color';
import { EventTypeServiceListItem } from 'presentations/api/schema/event_type_service';
import { EventTypeRestrictServiceUsageItem } from 'presentations/api/schema/event_type';
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import { useAboutPage } from '../../pages/AboutPage/hooks';

export interface Props {
  services: EventTypeServiceListItem[];
  isProvidedContact: boolean;
  restrictServiceUsages: EventTypeRestrictServiceUsageItem[] | null;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const isMobile = isMediaMax('tablet');

  return (
    <Container>
      {props.services.map((service, index) => {
        const usage = props.restrictServiceUsages?.find(
          (usage) => usage.serviceId === service.serviceId,
        );
        return (
          <RequiredServiceRightContainer key={index}>
            <ServiceRightUpperContainer>
              {service.coverImage && (
                <ServiceCoverImage
                  src={service.coverImage}
                  alt={service.name!}
                  width={isMobile ? 120 : 64}
                  height={isMobile ? 120 : 64}
                  fit="crop"
                />
              )}
              <ServiceNameContainer>
                <ServiceName>{service.name}</ServiceName>
                {Boolean(service.isShowPrice) && (
                  <ServicePrice>{formatTaxExcludedPrice(service)}</ServicePrice>
                )}
                <ServiceRestrictSummary>
                  {formatServiceRestrictSummary(service)}
                </ServiceRestrictSummary>
                {Boolean(props.isProvidedContact && usage) && (
                  <ServiceRestrictUsage>
                    {t('service restrict usage', {
                      times: usage?.userCurrentUsesCount,
                    })}
                  </ServiceRestrictUsage>
                )}
              </ServiceNameContainer>
            </ServiceRightUpperContainer>
            <ServiceDescription>{service.description}</ServiceDescription>
          </RequiredServiceRightContainer>
        );
      })}
    </Container>
  );
}

const Container = styled.div``;

const ServiceRightContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RequiredServiceRightContainer = styled(ServiceRightContainer)`
  border-bottom: 1px solid ${gray04};
  margin-bottom: ${spacing(16)};

  :last-of-type {
    border-bottom: none;
    margin-bottom: 0;
  }

  ${mediaMax.tablet} {
    margin-bottom: ${spacing(8)};
  }
`;
const ServiceNameContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const ServiceRightUpperContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const ServiceCoverImage = styled(Image)`
  margin-right: ${spacing(8)};
  width: 60px;
  height: 60px;
  object-fit: cover;
  ${mediaMax.tablet} {
    width: 64px;
    height: 64px;
  }
`;
const ServiceName = styled.div`
  font-size: ${fontSize(14)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  margin-top: ${spacing(8)};
  margin-bottom: ${spacing(4)};
  text-align: left;

  ${mediaMax.tablet} {
    font-size: ${fontSize(13)};
    margin-bottom: ${spacing(3)};
  }
`;
const ServicePrice = styled.div`
  font-size: ${fontSize(13)};
  font-weight: ${fontWeight('bold')};
  color: ${black};
  text-align: left;

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;
const ServiceRestrictSummary = styled.div`
  font-size: ${fontSize(13)};
  color: ${black};
  text-align: left;

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;
const ServiceRestrictUsage = styled.div`
  font-size: ${fontSize(13)};
  color: ${black};
  text-align: left;

  ${mediaMax.tablet} {
    font-size: ${fontSize(12)};
  }
`;
const ServiceDescription = styled.div`
  margin-top: ${spacing(8)};
  font-size: ${fontSize(13)};
  color: ${gray01};
  text-align: left;

  ${mediaMax.tablet} {
    margin-top: ${spacing(4)};
    font-size: ${fontSize(11)};
  }
`;
